import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BedDouble, Heart, MapPin } from "lucide-react";
import { inr } from "@/lib/format";
import { useResident } from "@/lib/residentAuth";
import { Card, EmptyState, ErrorState, ListSkeleton } from "@/components/common";
import { RoomAmenityChips } from "@/components/RoomAmenities";
import { CompareBar, RoomCompareSheet, useCompare } from "@/components/RoomCompare";
import { SaveHeart } from "@/components/ListingRooms";
import { useShortlist } from "@/pages/resident/useShortlist";
import { SavedTools } from "@/pages/resident/SavedTools";

function SavedRoom({ r, picked, onPick, onRemove }: { r: any; picked: boolean; onPick: () => void; onRemove: () => void }) {
  return (
    <div className={`py-3 border-t border-slate-100 first:border-0 ${picked ? "bg-[#e8f1fb]/50 -mx-4 px-4" : ""}`} data-testid={`saved-room-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold">Room {r.name}{r.floor ? <span className="text-xs font-semibold text-slate-500"> · Floor {r.floor}</span> : null}</p>
          <p className="text-xs text-slate-500">{r.sharing === 1 ? "Single room" : `${r.sharing}-sharing`} · Deposit {inr(r.deposit)}</p>
          <p className={`mt-1 text-xs font-semibold flex items-center gap-1 ${r.vacant_beds ? "text-green-700" : "text-slate-400"}`} data-testid={`saved-room-free-${r.id}`}>
            <BedDouble className="h-3.5 w-3.5" />{r.vacant_beds ? `${r.vacant_beds} of ${r.sharing} beds free` : "Full"}
          </p>
        </div>
        <p className="text-lg font-extrabold font-mono-num text-[#131e2e] shrink-0">{inr(r.rent_per_bed)}</p>
      </div>
      <div className="mt-2"><RoomAmenityChips items={r.amenities} testId={`saved-room-amenities-${r.id}`} /></div>
      <div className="mt-2.5 flex gap-2">
        <SaveHeart saved onClick={onRemove} testId={`saved-room-remove-${r.id}`} />
        <label data-testid={`saved-room-compare-${r.id}`} className={`flex-1 h-11 px-3 rounded-xl border text-sm font-semibold flex items-center gap-2 cursor-pointer select-none pg-tap ${picked ? "border-[#131e2e] bg-[#e8f1fb] text-[#131e2e]" : "border-slate-200 text-slate-600"}`}>
          <input type="checkbox" checked={picked} onChange={onPick} className="h-4 w-4 accent-[#131e2e]" />Compare
        </label>
      </div>
    </div>
  );
}

function groupByPG(rooms: any[]) {
  const map = new Map<string, any>();
  rooms.forEach((r) => {
    if (!map.has(r.property_id)) map.set(r.property_id, { id: r.property_id, name: r.pg_name, area: r.pg_area, rooms: [] });
    map.get(r.property_id).rooms.push(r);
  });
  return [...map.values()];
}

export default function Saved() {
  const nav = useNavigate();
  const { resident } = useResident();
  const { rooms, loading, error, reload, toggle } = useShortlist();
  const cmp = useCompare();
  const [open, setOpen] = useState(false);
  const remove = async (id: string) => { if (cmp.picked.includes(id)) cmp.toggle(id); await toggle(id); };
  const request = (r: any) => nav(`/resident/pg/${r.property_id}?room=${r.id}`);
  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Saved rooms</h1>
      <p className="text-sm text-slate-500 mb-4">Tick 2–3 rooms from any PG to compare them side by side.</p>
      {loading && !rooms ? <ListSkeleton /> : error ? <ErrorState message={error} onRetry={reload} /> : !rooms?.length ? (
        <EmptyState testId="saved-empty" icon={Heart} title="No saved rooms yet" text="Tap the heart on any room in a PG page to save it here."
          action={<Link to="/resident/search" data-testid="saved-find-btn" className="h-11 px-5 rounded-full bg-[#131e2e] text-white text-sm font-semibold grid place-items-center">Find a PG</Link>} />
      ) : (
        <div className="space-y-3 pg-stagger">
          <SavedTools rooms={rooms} />
          {groupByPG(rooms).map((g) => (
            <Card key={g.id} className="p-4" data-testid={`saved-pg-${g.id}`}>
              <Link to={`/resident/pg/${g.id}`} data-testid={`saved-pg-link-${g.id}`} className="block mb-1">
                <p className="font-bold text-lg truncate">{g.name}</p>
                {g.area && <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{g.area}</p>}
              </Link>
              {g.rooms.map((r) => <SavedRoom key={r.id} r={r} picked={cmp.picked.includes(r.id)} onPick={() => cmp.toggle(r.id)} onRemove={() => remove(r.id)} />)}
            </Card>
          ))}
          <CompareBar count={cmp.picked.length} onClear={cmp.clear} onOpen={() => setOpen(true)} />
        </div>
      )}
      {open && <RoomCompareSheet rooms={(rooms || []).filter((r) => cmp.picked.includes(r.id))} open={open} onClose={() => setOpen(false)} onRequest={resident?.stay ? undefined : request} />}
    </div>
  );
}
